import { inject, Injectable } from '@angular/core';
import {
    ExceptionDataService,
    ExceptionDto,
    ExceptionFacade,
    ExceptionTypeFacade,
} from '@pf/platform-wms/domain';
import { map, Observable, of, switchMap } from 'rxjs';
import { startOfDay } from 'date-fns';
import { CpwExceptionType } from '../models/cpw-exception-types';
import { asArraySafe } from '@pf/shared-utility';
import { CpwResolutionCustomFields } from '../models/cpw-resolution-custom-fields';
import { IDocumentDto, SelectOption } from '@pf/shared-common';
import { buildExceptionAgeDashboardStats } from '../utils/exception-age-dashboard-stats.util';

@Injectable({ providedIn: 'root' })
export class ExceptionsFacade {
    private readonly exceptionDataService = inject(ExceptionDataService);
    private readonly exceptionFacade = inject(ExceptionFacade);
    private readonly exceptionTypeFacade = inject(ExceptionTypeFacade);

    exceptionTypeConfigs = {
        [CpwExceptionType.PickingDamagedProduct]: {
            name: 'Damaged Product',
            color: 'var(--ant-danger-color)',
        },
    };

    exceptionStats$ = this.exceptionFacade.inProgressExceptions$.pipe(
        map(exceptions => {
            const exceptionDates = exceptions.map(exception =>
                startOfDay(new Date(exception.createdDate as string))
            );
            return buildExceptionAgeDashboardStats(exceptionDates, new Date());
        })
    );

    getExceptionTypeOptions(taskType: string): Observable<SelectOption[]> {
        return this.exceptionTypeFacade
            .whereCodeStartsWith$(taskType)
            .pipe(
                map(types =>
                    types.map(type => ({ label: type.name, value: type.id }))
                )
            );
    }

    createException$(exception: ExceptionDto, files: IDocumentDto[]) {
        return this.exceptionFacade.save$(exception).pipe(
            switchMap(exception => {
                return files.length > 0
                    ? this.exceptionFacade
                          .saveDocuments$(exception.id, files)
                          .pipe(map(() => exception))
                    : of(exception);
            })
        );
    }

    cancelResolution$(exceptionId: string) {
        const exception = this.exceptionFacade.getById(exceptionId);
        if (!exception) {
            return of(false);
        }

        return this.deleteResolutionCustomFields$(exception);
    }

    private deleteResolutionCustomFields$(exception: ExceptionDto) {
        const customFieldsToDelete = asArraySafe(exception.customFields)
            .filter(
                customField =>
                    customField.id &&
                    !!CpwResolutionCustomFields[
                        customField.key as CpwResolutionCustomFields
                    ]
            )
            .map(customField => customField.id as string);

        if (customFieldsToDelete.length === 0) {
            return of(true);
        }

        return this.exceptionDataService
            .deleteCustomFields$(exception.id, customFieldsToDelete)
            .pipe(map(() => true));
    }
}
